/**
 * useGetIncomingOrder.js
 *
 * Custom hook to fetch an incoming order on-demand.
 * - Declarative / callback-free
 * - Race-condition and abort-safe
 * - Skeleton/loading-friendly
 */

import { useEffect, useRef, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { GET_INCOMING_ORDER } from "../../../graphql/queries/IncomingOrdersContext";

/**
 * Extracts order details from the GraphQL query response.
 * Returns null if data is not available.
 *
 * @param {Object} data - Apollo query result
 * @returns {Object|null} - The extracted order details or null
 */
const extractOrder = (data) => data?.incomingOrdersContext?.details || null;

/**
 * useGetIncomingOrder
 *
 * Custom hook to fetch an incoming order on-demand.
 * - Declarative / callback-free
 * - Race-condition and abort-safe
 * - Skeleton/loading-friendly
 *
 * @returns {Object} - Contains:
 *   - getIncomingOrder: Function to trigger fetch by supplierOrderId
 *   - currentSupplierOrderId: The most recent supplierOrderId requested
 *   - incomingOrder: The fetched order data (or null)
 *   - incomingOrderLoading: Loading state
 *   - incomingOrderError: Error object (or null)
 */
const useGetIncomingOrder = () => {
  // AbortController to cancel in-flight requests
  const abortController = useRef(new AbortController());

  // Local state
  const [supplierOrderId, setSupplierOrderId] = useState(null);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const updateCachedIncomingOrder = (supplierOrder) => {

    setSupplierOrderId(supplierOrder.supplierOrderId);
    setOrder(supplierOrder);
    setLoading(false);
    setError(null);
  }

  /**
   * Abort any ongoing request and create a new AbortController
   */
  const abortRequest = () => {
    abortController.current.abort();
    abortController.current = new AbortController();
  };

  /**
   * Abort request when component unmounts
   */
  useEffect(() => {
    return () => {
      abortRequest();
    };
  }, []);

  /**
   * Apollo useLazyQuery for GET_INCOMING_ORDER
   */
  const [runQuery, { data, error: queryError, loading: queryLoading, called, refetch }] =
    useLazyQuery(GET_INCOMING_ORDER, {
      errorPolicy: "none",
      notifyOnNetworkStatusChange: true,
    });

  /**
   * Sync Apollo state with local state
   * - If extractOrder(data) is null, treat it as an error
   */
  useEffect(() => {
    if (!called) return;

    // Ignore abort errors
    if (queryError?.name === "AbortError") return;

    setLoading(queryLoading);

    const haveData = !queryLoading && data;
    const extractedOrder = haveData ? extractOrder(data) : null;
    const haveOrder = extractedOrder !== null;

    if (haveData && !haveOrder) {
      console.warn(`No order details found for id: ${supplierOrderId}\n`);
      setError(new Error("No order details found."));
      setOrder(null);
    } else {
      setError(queryError || null);
      setOrder(extractedOrder);
    }
  }, [data, queryError, queryLoading, called]);

  /**
   * Fetch incoming order on-demand
   *
   * @param {string|number} id - Supplier order ID
   * @returns {Promise} - Apollo query promise
   */
  const getIncomingOrder = async (id) => {
    if (!Boolean(id)) {
      console.warn(`getIncomingOrder called with invalid id: ${supplierOrderId}\n`);
      return;
    }

    const numericId = typeof id === "string" ? parseInt(id, 10) : id;

    if (isNaN(numericId)) {
      console.warn(`getIncomingOrder called with invalid id: ${supplierOrderId}\n`);
      return;
    }

    abortRequest(); // Cancel previous request

    setSupplierOrderId(id); // Save latest ID
    setOrder(null);         // Reset order immediately (for skeleton UI)
    setLoading(true);       // Show loading state
    setError(null);         // Reset error

    return runQuery({
      context: { fetchOptions: { signal: abortController.current.signal } },
      variables: { supplierOrderId: numericId },
    });
  };

  return {
    getIncomingOrder,
    refetchIncomingOrder: refetch,
    currentSupplierOrderId: supplierOrderId,
    incomingOrder: order,
    incomingOrderLoading: loading,
    incomingOrderError: error,
    updateCachedIncomingOrder
  };
};

export default useGetIncomingOrder;
